'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchSignedUrl as defaultFetchSignedUrl } from '@lib/image-client';
import { getOptimizedImageUrl as defaultGetOptimizedImageUrl } from '@lib/image-utils';
interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string; // can be a full URL or an image id
  storagePath?: string; // optional explicit storage path for signed URL
  // Optional helpers for app-specific image resolution; safe no-ops are used when omitted.
  getOptimizedImageUrl?: (src: string, kind?: string) => string;
  fetchSignedUrl?: (path: string) => Promise<string | null | undefined>;
}

export default function ImageWithSignedFallback({
  src,
  storagePath,
  getOptimizedImageUrl,
  fetchSignedUrl,
  alt,
  onError,
  ...rest
}: Props) {
  const resolveOptimized = useMemo(
    () => getOptimizedImageUrl ?? defaultGetOptimizedImageUrl,
    [getOptimizedImageUrl]
  );
  const resolveSigned = useMemo(() => fetchSignedUrl ?? defaultFetchSignedUrl, [fetchSignedUrl]);
  const lastSrcRef = useRef<string | null>(null);
  const triedLocalRef = useRef(false);
  const triedOptimizedRef = useRef(false);
  const [currentSrc, setCurrentSrc] = useState<string>(() => {
    // If src looks like a URL, prefer it; otherwise ask image-utils for optimized URL
    if (/^https?:\/\//i.test(String(src))) return src;
    const clean = String(src)
      .replace(/\.[^/.]+$/, '')
      .replace(/^\//, '');
    return `/generated-images/${clean}.webp`;
  });
  const [triedSigned, setTriedSigned] = useState(false);

  useEffect(() => {
    if (lastSrcRef.current !== src) {
      lastSrcRef.current = src;
      setTriedSigned(false);
      triedLocalRef.current = false;
      triedOptimizedRef.current = false;
    }
    if (/^https?:\/\//i.test(String(src))) {
      setCurrentSrc(src);
      return;
    }

    const clean = String(src)
      .replace(/\.[^/.]+$/, '')
      .replace(/^\//, '');
    triedLocalRef.current = true;
    setCurrentSrc(`/generated-images/${clean}.webp`);
  }, [src, storagePath, resolveOptimized, resolveSigned]);

  const handleError = async (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (onError) onError(e as any);

    if (!triedOptimizedRef.current && typeof src === 'string' && !/^https?:\/\//i.test(src)) {
      const resolved = resolveOptimized(src, 'post');
      triedOptimizedRef.current = true;
      if (resolved && resolved !== currentSrc) {
        setCurrentSrc(resolved);
        return;
      }
    }

    if (triedSigned) return;

    // Determine storage path: prefer explicit prop, otherwise extract filename
    let path = storagePath || '';
    if (!path) {
      // If the current source is a local generated image path, map it
      // back to the S3 storage key (garden_images/<name>.jpg)
      if (typeof currentSrc === 'string' && currentSrc.startsWith('/generated-images/')) {
        const file = currentSrc.split('/').pop() || '';
        const clean = file.replace(/\.[^/.]+$/, '');
        path = `garden_images/${clean}.jpg`;
      } else {
        try {
          const u = new URL(currentSrc);
          path = u.pathname.replace(/^\//, '');
        } catch (err) {
          // src may be an id like 'tomate-cherry-sungold-01.jpg' or 'tomate-cherry-sungold-01'
          const clean = String(src)
            .replace(/\.[^/.]+$/, '')
            .replace(/^\//, '');
          path = `garden_images/${clean}.jpg`;
        }
      }
    }

    if (!path) return;

    setTriedSigned(true);
    try {
      const signed = await resolveSigned(path);
      if (signed) {
        setCurrentSrc(signed);
      }
    } catch (err) {
      // ignore — leave placeholder or broken image
    }
  };

  // If we have no resolved source (image id missing or utils returned empty),
  // render a neutral placeholder instead of showing the site logo.
  if (!currentSrc) {
    const placeholderStyle = (rest as any)?.style || {};
    const placeholderClass = (rest as any)?.className || '';
    return (
      <div
        role='img'
        aria-label={alt || ''}
        className={`image-missing ${placeholderClass}`}
        style={{
          backgroundColor: 'transparent',
          display: 'block',
          width: placeholderStyle.width || '100%',
          height: placeholderStyle.height || '100%',
          objectFit: (placeholderStyle as any).objectFit || 'cover',
          ...placeholderStyle,
        }}
      />
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img src={currentSrc} alt={alt} onError={handleError} {...rest} />
  );
}
